<template>
  <div class="core-input-range-wrapper">
    <input
      type="range"
      :aria-label="props.ariaLabel"
      class="core-input-range"
      :class="{ 'is-disabled': props.disabled }"
      :value="props.modelValue"
      :tabindex="props.tabindex"
      :min="props.min"
      :max="props.max"
      :step="props.step"
      :disabled="props.disabled"
      :style="{
        '--range-thumb-border-color': thumbBorderColor,
        '--range-disabled-thumb-border-color': activeTrackColor,
        backgroundImage: `linear-gradient(to right,
        ${activeTrackColor} ${((props.modelValue - props.min) / (props.max - props.min)) * 100}%,
        #d4d4d4 ${((props.modelValue - props.min) / (props.max - props.min)) * 100}%,
        #d4d4d4 100%)`,
      }"
      @input="onInput"
      @change="onChange" />
    <div
      v-if="props.thumbIcon === 'lock'"
      class="pointer-events-none absolute left-0 top-1/2 z-10 -translate-x-1/2 -translate-y-1/2 text-neutral-500"
      :style="{ left: thumbPosition }">
      <IconLock class="h-3 w-3" stroke="2" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { IconLock } from '@tabler/icons-vue'

const props = withDefaults(
  defineProps<{
    modelValue: number
    ariaLabel?: string
    min: number
    max: number
    step: number
    tabindex?: number
    colorVariant?: 'neutral' | 'primary'
    disabled?: boolean
    thumbIcon?: 'lock'
  }>(),
  {
    colorVariant: 'primary',
    disabled: false,
  }
)

const activeTrackColor = computed(() => (props.colorVariant === 'primary' ? 'var(--color-primary-dark)' : '#6e6e6e'))

const thumbBorderColor = computed(() => (props.colorVariant === 'primary' ? 'var(--color-primary-dark)' : '#333333'))

const thumbPosition = computed(() => {
  const progress = (props.modelValue - props.min) / (props.max - props.min)

  return `calc(${progress * 100}% - ${progress * 1.5}rem + 0.75rem)`
})

const emit = defineEmits(['update:modelValue', 'input', 'change'])

function onInput(evt: Event): void {
  const value = (evt.target as HTMLInputElement).value
  emit('update:modelValue', Number(value))
  emit('input', evt)
}

function onChange(evt: Event): void {
  emit('change', evt)
}
</script>

<style lang="postcss" scoped>
.core-input-range-wrapper {
  @apply relative mt-1 mb-4 lg:mb-8;
}

.core-input-range {
  @apply w-full rounded-xl bg-transparent px-0;
  -webkit-appearance: none;
  &:focus {
    outline: none;
  }
  &:focus::-moz-focus-outer {
    border: 0;
  }

  /** Range bar thumb styling */
  &::-webkit-slider-thumb {
    border: 2px solid var(--range-thumb-border-color, #333333);
    @apply bg-white;
    @apply -mt-1 h-6 w-6 cursor-pointer rounded-full shadow-1-4-gray;
    -webkit-appearance: none;
  }
  /** FireFox */
  &::-moz-range-thumb {
    border: 2px solid var(--range-thumb-border-color, #333333);
    @apply bg-white;
    @apply -mt-1 h-6 w-6 cursor-pointer rounded-full shadow-1-4-gray;
  }

  /** Range track styling */
  &::-webkit-slider-runnable-track {
    @apply h-4 w-full cursor-pointer rounded-md bg-transparent;
  }
  &::-moz-range-track {
    @apply h-4 w-full cursor-pointer rounded-md bg-transparent;
  }

  &::-ms-fill-upper {
    @apply rounded-md bg-transparent;
  }

  &.is-disabled {
    @apply opacity-70;
  }

  &.is-disabled::-webkit-slider-thumb {
    border: 2px solid var(--range-disabled-thumb-border-color, #6e6e6e);
    @apply cursor-not-allowed bg-neutral-100;
  }

  &.is-disabled::-moz-range-thumb {
    border: 2px solid var(--range-disabled-thumb-border-color, #6e6e6e);
    @apply cursor-not-allowed bg-neutral-100;
  }

  &.is-disabled::-webkit-slider-runnable-track {
    @apply cursor-not-allowed;
  }

  &.is-disabled::-moz-range-track {
    @apply cursor-not-allowed;
  }
}
</style>
